// Copyright (c) Bentley Systems

export type OnPremClientHealthStatus =
  | "Unknown"
  | "Healthy"
  | "Degraded"
  | "Unhealthy";

/** Last known execution outcome of one integration project on one on-prem client within one iTwin. */
export type ProjectRunStatus = {
  projectId: string;
  projectName?: string;
  /** The iTwin this run was executed under. */
  iTwinId: string;
  lastRunSuccess: boolean;
  lastRun: string;
  /** Permanently excluded from health-icon computation (e.g. a known test project). */
  isMuted: boolean;
};

/** On-prem clients (not iTwins) are the primary grouping for this feature. */
export type ClientTelemetryOverview = {
  mqttClientName: string;
  installedVersion?: string;
  lastUsed?: string;
  /** Runs recorded for the client regardless of the requested window; only the cross-iTwin overview provides it. */
  trackedProjectCount?: number;
  /** Runs inside the requested window. */
  projects: ProjectRunStatus[];
  /**
   * From the client's registrations (CredentialsIdentity), not the telemetry data itself. A client can be
   * registered in several iTwins, each with its own value, so this holds every distinct value, comma-separated.
   */
  notificationEmail?: string;
  /** From the client's registrations; every distinct value, " | "-separated (see `notificationEmail`). */
  notes?: string;
  /** Whether the registrations were fetched; they are loaded only when the client is expanded. */
  isRegistrationLoaded?: boolean;
  /** Some registrations could not be fetched, e.g. without access to one of the client's iTwins. */
  isRegistrationUnavailable?: boolean;
};

/** Identifies one (client, project, iTwin) row for bulk mute/unmute/delete operations. */
export type OnPremProjectKey = {
  mqttClientName: string;
  projectId: string;
  iTwinId: string;
};

/** A single (client, project, iTwin) row, used by the per-client project runs table. */
export type ClientProjectRow = OnPremProjectKey & {
  projectName?: string;
  lastRunSuccess: boolean;
  lastRun: string;
  isMuted: boolean;
};

/** Day-window options for the health-icon/staleness filter. 0 means "All time". */
export const DAY_FILTER_OPTIONS = [1, 7, 30, 0] as const;
export type DayFilterValue = (typeof DAY_FILTER_OPTIONS)[number];

/** How long EDFS keeps run records; its `OnPremClientProjectRunRetention.sql` maintenance script deletes older ones. */
export const ON_PREM_RUN_RETENTION_DAYS = 365;

export const dayFilterLabel = (value: DayFilterValue): string =>
  value === 0 ? "All time" : `Last ${value} day${value === 1 ? "" : "s"}`;

/** Whether a project's last run falls within the selected day window. `0` means "All time". */
export const isWithinDayWindow = (lastRun: string, dayFilter: DayFilterValue): boolean => {
  if (dayFilter === 0) {
    return true;
  }
  const cutoffMs = Date.now() - dayFilter * 24 * 60 * 60 * 1000;
  return new Date(lastRun).getTime() >= cutoffMs;
};

/** One client's project runs whose latest run falls in the day window, as rows for the project runs table. */
export const toClientProjectRows = (
  mqttClientName: string,
  projects: ProjectRunStatus[],
  dayFilter: DayFilterValue,
): ClientProjectRow[] =>
  projects
    .filter((p) => isWithinDayWindow(p.lastRun, dayFilter))
    .map((p) => ({ ...p, mqttClientName }));

/**
 * Projects that qualify for health-icon/tooltip computation under the given day window: muted
 * projects are always excluded, and (when a day window is selected) projects last run outside
 * that window are excluded too. Shared by `computeClientHealthStatus` and the health-status
 * tooltip so their counts can never diverge.
 */
export const getQualifyingProjects = (
  projects: ProjectRunStatus[],
  dayFilter: DayFilterValue,
): ProjectRunStatus[] =>
  projects.filter((p) => !p.isMuted && isWithinDayWindow(p.lastRun, dayFilter));

/**
 * Computes a client's health icon from its projects: muted projects are always excluded, and
 * (when a day window is selected) projects last run outside that window are excluded too.
 * Grey ("Unknown") when no qualifying project remains.
 */
export const computeClientHealthStatus = (
  projects: ProjectRunStatus[],
  dayFilter: DayFilterValue,
): OnPremClientHealthStatus => {
  const qualifying = getQualifyingProjects(projects, dayFilter);

  if (qualifying.length === 0) {
    return "Unknown";
  }
  const failedCount = qualifying.filter((p) => !p.lastRunSuccess).length;
  if (failedCount === 0) {
    return "Healthy";
  }
  if (failedCount === qualifying.length) {
    return "Unhealthy";
  }
  return "Degraded";
};
